import React, { useEffect, useMemo, useState } from 'react';
import { ContactCreate, ContactRead, ContactUpdate } from '../types/api';
import { apiService } from '../services/api';
import { Plus, Edit2, Trash2, Users } from 'lucide-react';

interface ContactFormProps {
  initialData?: ContactRead;
  onSubmit: (data: ContactCreate | ContactUpdate) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

const EMPTY_FORM: ContactCreate = {
  name: '',
  address: '',
  phone: '',
  email: '',
  notes: '',
  birthday: '',
};

const ContactForm: React.FC<ContactFormProps> = ({ initialData, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState<ContactCreate>(initialData ? {
    name: initialData.name,
    address: initialData.address || '',
    phone: initialData.phone || '',
    email: initialData.email || '',
    notes: initialData.notes || '',
    birthday: initialData.birthday || '',
  } : EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof ContactCreate) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    } else if (formData.name.trim().length > 200) {
      nextErrors.name = 'Name must be 200 characters or less';
    }

    if (formData.address && formData.address.length > 500) {
      nextErrors.address = 'Address must be 500 characters or less';
    }

    if (formData.phone && formData.phone.length > 50) {
      nextErrors.phone = 'Phone must be 50 characters or less';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (formData.notes && formData.notes.length > 1000) {
      nextErrors.notes = 'Notes must be 1000 characters or less';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    await onSubmit({
      ...formData,
      address: formData.address || undefined,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      notes: formData.notes || undefined,
      birthday: formData.birthday || undefined,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={handleChange('name')}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
          maxLength={200}
          autoFocus
        />
        {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone ?? ''}
            onChange={handleChange('phone')}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'}`}
            maxLength={50}
          />
          {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.email ?? ''}
            onChange={handleChange('email')}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
          />
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Birthday
          </label>
          <input
            type="date"
            value={formData.birthday || ''}
            onChange={handleChange('birthday')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <input
            type="text"
            value={formData.address ?? ''}
            onChange={handleChange('address')}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.address ? 'border-red-300' : 'border-gray-300'}`}
            maxLength={500}
          />
          {errors.address && <p className="text-sm text-red-600 mt-1">{errors.address}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes ?? ''}
          onChange={handleChange('notes')}
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.notes ? 'border-red-300' : 'border-gray-300'}`}
          maxLength={1000}
        />
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          {errors.notes && <span className="text-red-600">{errors.notes}</span>}
          <span>{formData.notes?.length || 0}/1000</span>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Contact'}
        </button>
      </div>
    </form>
  );
};

export const ContactsManager: React.FC = () => {
  const [contacts, setContacts] = useState<ContactRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactRead | null>(null);
  const [showForm, setShowForm] = useState(false);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.listContacts();
      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const handleCreate = () => {
    setEditingContact(null);
    setShowForm(true);
  };

  const handleEdit = (contact: ContactRead) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleDelete = async (contactId: string) => {
    if (!window.confirm('Delete this contact? This cannot be undone.')) {
      return;
    }
    try {
      await apiService.deleteContact(contactId);
      setContacts((prev) => prev.filter((contact) => contact.id !== contactId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleSubmit = async (data: ContactCreate | ContactUpdate) => {
    setSubmitting(true);
    try {
      if (editingContact) {
        const updated = await apiService.updateContact(editingContact.id, data);
        setContacts((prev) => prev.map((contact) => contact.id === updated.id ? updated : contact));
      } else {
        const created = await apiService.createContact(data as ContactCreate);
        setContacts((prev) => [created, ...prev]);
      }
      setShowForm(false);
      setEditingContact(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setSubmitting(false);
    }
  };

  const visibleContacts = useMemo(() => contacts.filter((contact) => contact.is_active), [contacts]);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users size={18} className="text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Contacts</h3>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" />
          Add Contact
        </button>
      </div>

      {showForm && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <ContactForm
            initialData={editingContact || undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingContact(null);
            }}
            submitting={submitting}
          />
        </div>
      )}

      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        ) : visibleContacts.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">
            No contacts yet. Create one to start attaching people to your notes.
          </div>
        ) : (
          visibleContacts.map((contact) => (
            <div
              key={contact.id}
              className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">{contact.name}</p>
                <div className="text-xs text-gray-500 space-x-2 mt-1">
                  {contact.phone && <span>{contact.phone}</span>}
                  {contact.email && <span>{contact.email}</span>}
                </div>
                {contact.notes && (
                  <p className="text-xs text-gray-600 mt-1">
                    {contact.notes}
                  </p>
                )}
              </div>
              <div className="flex space-x-2 mt-3 sm:mt-0">
                <button
                  onClick={() => handleEdit(contact)}
                  className="p-2 text-gray-500 hover:text-blue-600 rounded-md hover:bg-gray-100"
                  title="Edit contact"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(contact.id)}
                  className="p-2 text-gray-500 hover:text-red-600 rounded-md hover:bg-gray-100"
                  title="Delete contact"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

